import { API_BASE_URL, AUTH_MODE, ApiError, cookieRequest } from '@/services/api';

export interface HomeSettings {
  heroImage: string;
  heroEyebrow: string;
  heroTitle: string;
  heroButtonText: string;
  heroBrandText: string;
  usesDefaultImage: boolean;
  updatedAt: string;
}

export interface HomeSettingsUpdate {
  heroEyebrow: string;
  heroTitle: string;
  heroButtonText: string;
  heroBrandText: string;
}

export const DEFAULT_HOME_SETTINGS: HomeSettings = {
  heroImage: 'https://readdy.ai/api/search-image?query=Warm%20cozy%20family%20moment%20portrait%20of%20happy%20parents%20and%20two%20children%20sitting%20together%20on%20cream%20colored%20sofa%20in%20bright%20living%20room%2C%20soft%20natural%20window%20light%20creating%20gentle%20highlights%2C%20beige%20and%20cream%20neutral%20color%20palette%2C%20genuine%20smiles%20and%20loving%20expressions%2C%20editorial%20family%20lifestyle%20photography%20with%20clean%20composition%20and%20soft%20atmosphere%2C%20simple%20minimal%20interior%20background&width=750&height=1000&seq=home-hero-family&orientation=portrait',
  heroEyebrow: '우리 가족의 오늘',
  heroTitle: '새로운 추억을\n남겨보세요',
  heroButtonText: '사진 올리기',
  heroBrandText: '우리 가족 앨범',
  usesDefaultImage: true,
  updatedAt: '',
};

const MOCK_KEY = 'bongnas_home_settings';
export const HOME_SETTINGS_CACHE_KEY = 'bongnas_home_settings_cache_v2';
export const HOME_SETTINGS_EVENT = 'bongnas:home-settings-updated';

function normalizeSettings(value: Partial<HomeSettings> | null | undefined): HomeSettings {
  return {
    ...DEFAULT_HOME_SETTINGS,
    ...(value || {}),
  };
}

export function readCachedHomeSettings(): HomeSettings {
  if (typeof window === 'undefined') return DEFAULT_HOME_SETTINGS;
  try {
    const raw = window.localStorage.getItem(HOME_SETTINGS_CACHE_KEY);
    return raw ? normalizeSettings(JSON.parse(raw)) : DEFAULT_HOME_SETTINGS;
  } catch {
    return DEFAULT_HOME_SETTINGS;
  }
}

function cacheHomeSettings(settings: HomeSettings): HomeSettings {
  const normalized = normalizeSettings(settings);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(HOME_SETTINGS_CACHE_KEY, JSON.stringify(normalized));
    } catch {
      // 저장 공간 문제는 화면 표시를 막지 않습니다.
    }
  }
  return normalized;
}

function readMockSettings(): HomeSettings {
  try {
    const raw = localStorage.getItem(MOCK_KEY);
    return raw ? normalizeSettings(JSON.parse(raw)) : DEFAULT_HOME_SETTINGS;
  } catch {
    return DEFAULT_HOME_SETTINGS;
  }
}

function extractSettings(body: unknown): HomeSettings {
  if (!body || typeof body !== 'object') {
    throw new ApiError('홈 화면 설정 응답이 올바르지 않습니다.', 500);
  }
  const record = body as Record<string, unknown>;
  const rawSettings = (record.settings ?? record) as Partial<HomeSettings>;
  return cacheHomeSettings(normalizeSettings(rawSettings));
}

async function parseResponse(response: Response): Promise<HomeSettings> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // 아래 오류 처리에서 안내합니다.
  }
  if (!response.ok) {
    const record = body && typeof body === 'object' ? body as Record<string, unknown> : {};
    const message = String(record.detail || record.message || '홈 화면 설정을 처리하지 못했습니다.');
    throw new ApiError(message, response.status);
  }
  return extractSettings(body);
}

export async function getHomeSettings(): Promise<HomeSettings> {
  if (AUTH_MODE === 'mock') return cacheHomeSettings(readMockSettings());

  try {
    const response = await fetch(`${API_BASE_URL}/api/site-settings?_=${Date.now()}`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    });
    return await parseResponse(response);
  } catch (error) {
    const cached = readCachedHomeSettings();
    if (cached.updatedAt || cached.heroEyebrow !== DEFAULT_HOME_SETTINGS.heroEyebrow) {
      return cached;
    }
    throw error;
  }
}

export async function updateHomeSettings(payload: HomeSettingsUpdate): Promise<HomeSettings> {
  if (AUTH_MODE === 'mock') {
    const settings = cacheHomeSettings({
      ...readMockSettings(),
      ...payload,
      updatedAt: new Date().toISOString(),
    });
    localStorage.setItem(MOCK_KEY, JSON.stringify(settings));
    return settings;
  }
  const response = await cookieRequest<{ settings: HomeSettings }>('/api/admin/site-settings', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return cacheHomeSettings(response.settings);
}

export async function uploadHomeHeroImage(file: File): Promise<HomeSettings> {
  if (AUTH_MODE === 'mock') {
    const settings = cacheHomeSettings({
      ...readMockSettings(),
      heroImage: URL.createObjectURL(file),
      usesDefaultImage: false,
      updatedAt: new Date().toISOString(),
    });
    localStorage.setItem(MOCK_KEY, JSON.stringify(settings));
    return settings;
  }
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(`${API_BASE_URL}/api/admin/site-settings/hero-image`, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    body: formData,
  });
  return parseResponse(response);
}

export async function resetHomeHeroImage(): Promise<HomeSettings> {
  if (AUTH_MODE === 'mock') {
    const settings = cacheHomeSettings({
      ...readMockSettings(),
      heroImage: DEFAULT_HOME_SETTINGS.heroImage,
      usesDefaultImage: true,
      updatedAt: new Date().toISOString(),
    });
    localStorage.setItem(MOCK_KEY, JSON.stringify(settings));
    return settings;
  }
  const response = await fetch(`${API_BASE_URL}/api/admin/site-settings/hero-image`, {
    method: 'DELETE',
    credentials: 'include',
    cache: 'no-store',
  });
  return parseResponse(response);
}
